import {shallowEqual, useDispatch, useSelector} from 'react-redux';

import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import loadAds from '@jobseeker/common-core/helpers/loadAds';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import jobFeedService from '@jobseeker/common-core/services/jobfeed.service';
import {AppState, wrapper} from '@jobseeker/common-core/stores';
import {VersionVariants} from '@jobseeker/lotus/enums';

import {Chat} from '@components';
import {metaInfo} from '@configs';
import {JOBFEED, TAG, TAG_ID_HIRIST} from '@constants';
import {loadSEO} from '@helpers';
import {JobFeed, NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {
  getConfigByApp,
  getIdBySlug,
  getLocationIds,
  isUserAgentForBot,
  removedUndefinedFromArrayObject,
  slugToTitle,
  utilServerSideDeviceDetection,
} from '@utils';
import {getListSEOContent, getPageUrl} from '@utils/seo';

import cookie from 'cookie';

import type {GetServerSidePropsContext} from 'next/types';
import {useEffect, useRef, useState} from 'react';
import {TrackEventUBA} from '@jobseeker/common-core/utils/analytics';
import {useRouter} from 'next/router';
import {gladiatorService} from '@jobseeker/common-core/services';
import {
  COOKIE_LOGIN_AUTH,
  HIRIST_CATEGORY_BLOG_ID_MAPPING,
  HIRIST_CATEGORY_ID_MAPPING,
} from '@jobseeker/common-core/constants';
import jobfeedService from '@jobseeker/common-core/services/jobfeed.service';
import {AdsElementType} from '@jobseeker/common-core/types';

const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

type TagFeedPageProps = {
  userIdSSR: number;
  tagTitle: string;
  tagId: string;
  catId: string;
  metaData: object;
  seoConfig: object;
  filters: {
    page: number;
    loc: string[];
    industry: string[];
  };
};

const TagJobFeedPage = ({
  userIdSSR,
  tagTitle,
  tagId,
  catId,
  metaData,
  seoConfig,
  filters,
}: TagFeedPageProps) => {
  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();

  useValidateAuth(dispatch);
  const router = useRouter();
  const {userId} = useSelector((state: AppState) => state.auth, shallowEqual);

  dispatch({
    type: 'AppContext/set_recap',
    payload: tagId,
  });

  const breadcrumbElements = [
    {
      name: 'Home',
      item: '/',
    },
    {
      name: isAppIimjobs ? tagTitle : `${tagTitle} Jobs`,
      item: '',
    },
  ];
  const hasTracked = useRef(false);
  const initialPageTracked = useRef(false);
  const hasPageNoChanged = useRef(false);
  const hasFilterChanged = useRef(false);

  const [pageNumber, setPageNumber] = useState(filters?.page + 1);
  let sessionFilter: {minexp?: string; maxexp?: string} = {};
  try {
    sessionFilter = JSON.parse(localStorage.getItem('filter') || '{}');
  } catch (error) {}
  const [currentFilters, setCurrentFilters] = useState<{
    loc?: string[];
    minexp?: string;
    maxexp?: string;
    industry?: string[];
  }>({
    loc: filters.loc,
    minexp: (router.query.minexp as string) || (sessionFilter.minexp as string),
    maxexp: (router.query.maxexp as string) || (sessionFilter.maxexp as string),
    industry: filters.industry,
  });

  // Reset tracking when page number changes
  useEffect(() => {
    hasPageNoChanged.current = true;
  }, [pageNumber]);

  // Reset tracking when filter changes
  useEffect(() => {
    if (initialPageTracked.current) {
      hasFilterChanged.current = true;
    }
  }, [currentFilters]);

  // console.log("page: ", filters.page);

  useEffect(() => {
    if (
      hasTracked.current &&
      !hasPageNoChanged.current &&
      !hasFilterChanged.current
    )
      return;
    let actionType: string =
      pageNumber > filters.page + 1
        ? 'scroll'
        : initialPageTracked.current
        ? 'scroll'
        : 'view';
    if (hasFilterChanged.current) {
      actionType = 'filter';
    }

    const sessionUserId = userIdSSR || userId || '-1';

    hasTracked.current = true;
    hasPageNoChanged.current = false;
    hasFilterChanged.current = false;
    TrackEventUBA('tagPageView', actionType, {
      ref: router.query.ref || '',
      // filterExp: router.query.minexp
      //   ? `${router.query.minexp}-${router.query.maxexp}`
      //   : '',
      filterExp: currentFilters?.maxexp
        ? `${currentFilters.minexp}-${currentFilters.maxexp}`
        : '',
      filterLocation: currentFilters?.loc?.join(',') || '',
      filterIndustry: currentFilters?.industry?.join(',') || '',
      pageName: 'Tag Page',
      pageNo: pageNumber,
      tagID: Number(tagId) || '',
      categoryID: Number(catId) || '',
      tagName: tagTitle,
      sessionUsername: `${sessionUserId}`,
    });
    initialPageTracked.current = true;
  }, [userId, pageNumber, currentFilters]);

  return (
    <>
      <Interceptor metaInfo={metaData}>
        <SeoLayout seoConfig={seoConfig} paginationEnabled={true} />
        <NavLayout>
          <ResponsiveLayout
            variant={VersionVariants.VERSION_TWO}
            breadcrumbElements={breadcrumbElements}
          >
            <JobFeed
              jobFeedType={TAG}
              catOrTagId={tagId}
              catOrTagTitle={tagTitle}
              showProfileCard={false}
              showFeatured={true}
              showLearning={true}
              preFilters={filters}
              setPageNumber={setPageNumber}
              onFiltersChange={setCurrentFilters}
            />
          </ResponsiveLayout>
        </NavLayout>
      </Interceptor>
      <Chat />
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    const pageNo = parseInt(context.query?.page) || 0;
    const minExp = parseInt(context.query?.minexp) || 0;
    const maxExp = parseInt(context.query?.maxexp) || 0;
    const posting = parseInt(context.query?.posting);
    const location = getLocationIds(context.query?.loc || '');
    const industry = context.query?.industry?.split(',') || [];
    const tabSlug = context.params?.slug || context.query?.slug;
    const slugUrl = context?.resolvedUrl || '';
    const tag = context.query?.tag;
    const metaData = getConfigByApp(metaInfo, JOBFEED, true, TAG);

    let userId = -1;

    try {
      if (context.req?.headers?.cookie) {
        const cookies = cookie.parse(context.req.headers.cookie);

        if (cookies?.[COOKIE_LOGIN_AUTH]) {
          const validateInfo = await gladiatorService.validate('validate', {
            headers: {
              cookie: context.req?.headers?.cookie,
            },
          });

          userId = parseInt(validateInfo?.userId ?? '0', 10);
        }
      }
    } catch (err) {}

    const {
      header: {
        meta: {tabsList},
      },
      ads: {
        featuredEmployer,
        diversity,
        learnGrow,
        featuredInstitutes,
        learningPartners,
      },
    } = metaData || {};

    const {id, title, catId, subCatId} = getIdBySlug(
      'tag',
      tabSlug,
      tabsList,
      false,
      process.env.NEXT_PUBLIC_APP_NAME === 'hirist' ? true : false
    );

    let slugCatId = catId;
    let slugId = id;
    let slugTitle = title;
    const TEMP_CATEGORY_MAPPER: {[key: string]: string} = {
      legal: '/c/legal-jobs',
    };

    if (!id && !title) {
      const slugFiltered = tabSlug?.replace(/-jobs$/, '');

      const newUrl = TEMP_CATEGORY_MAPPER[slugFiltered];

      if (newUrl) {
        return {
          redirect: {
            permanent: true,
            destination: newUrl,
          },
        };
      }
    }
    if (!slugId) {
      const tagInfo = await jobFeedService.tagIdBySlug(
        tag || tabSlug?.replace(/-jobs$/, '')
      );

      slugId = tagInfo?.id;
      slugTitle = tagInfo?.title;
      slugCatId = tagInfo?.catId;
    }
    if (!slugId || slugId?.length <= 0) {
      const hirist_extra = TAG_ID_HIRIST.get(tabSlug);
      if (hirist_extra) {
        slugId = hirist_extra.id;
        slugTitle = hirist_extra.tagName;
      }
    }
    const locationIds = location
      ? location?.split(',')?.map(v => parseInt(v))
      : null;
    const filters = {
      page: pageNo > 0 ? pageNo - 1 : 0,
      query: slugId || '-1',
      minexp: minExp,
      maxexp: maxExp,
      loc: locationIds,
      industry,
      posting,
    };
    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: TAG},
    });

    const {isMobile} = utilServerSideDeviceDetection(context);
    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }

    store.dispatch({
      type: 'AppContext/set_context_url',
      payload: context?.resolvedUrl?.split('?')[0] || '/',
    });

    if (slugCatId && slugCatId > -1) {
      store.dispatch({
        type: 'AppContext/set_navigation_selector',
        payload: slugCatId,
      });

      store.dispatch({
        type: 'AppContext/set_category_actual',
        payload: subCatId && subCatId > 0 ? subCatId : slugCatId,
      });
    }

    if (slugId) {
      store.dispatch({
        type: 'AppContext/set_tag_selector',
        payload: slugId,
      });
    }

    if (typeof window === 'undefined') {
      store.dispatch({
        type: 'AppContext/set_enable_ads_preload',
        payload: true,
      });
    }

    store.dispatch({type: 'job/jobFeed/set_initial_state'});

    const isBotUser = isUserAgentForBot(context);
    let totalJobs = 0;

    if (isBotUser) {
      /**
       * Fetch the initial state for jobs list and related ads to show
       */
      store.dispatch({
        type: 'AppContext/set_bot',
        payload: true,
      });

      try {
        const {
          auth: {userId},
        } = store.getState();

        const jobFeeds: any = await jobFeedService.getTagJobs({
          userId,
          pageNo: pageNo > 0 ? pageNo - 1 : 0,
          query: slugId,
          minexp: minExp,
          maxexp: maxExp,
          loc: location,
          size: 100,
        });
        totalJobs = jobFeeds?.totalJobs;

        store.dispatch({
          type: 'job/jobFeed/set_jobfeed',
          payload: jobFeeds,
        });
      } catch (error) {
        console.log(
          '🚀 ~ file: [slug].tsx:200 ~ store jobfeed tags jobs=> ~ error:',
          error
        );
        return {
          notFound: true,
        };
      }

      try {
        if (featuredEmployer?.slot) {
          const ads = await loadAds(featuredEmployer.slot, {
            categoryIds: slugCatId?.toString(),
          });
          if (ads) {
            store.dispatch({
              type: 'Ads/set_featured_employer',
              payload: removedUndefinedFromArrayObject(ads),
            });
          }
        }

        if (diversity?.slot) {
          const ads = await loadAds(diversity.slot);
          if (ads) {
            store.dispatch({
              type: 'Ads/set_diversity',
              payload: removedUndefinedFromArrayObject(ads),
            });
          }
        }

        if (learnGrow?.slot) {
          const ads = await loadAds(
            learnGrow.slot,
            {
              categoryIds: slugCatId?.toString(),
              page: 0,
              size: 20,
              user_location: 0,
              shuffle: 1,
              orderby: 'modified',
            },
            true
          );

          if (ads) {
            store.dispatch({
              type: 'Ads/set_learn_grow',
              payload: removedUndefinedFromArrayObject(ads),
            });
          }
        }

        if (featuredInstitutes?.slot) {
          const ads = await loadAds(featuredInstitutes.slot, {
            categoryIds: slugCatId?.toString(),
          });
          if (ads) {
            store.dispatch({
              type: 'Ads/set_featured_institutes',
              payload: removedUndefinedFromArrayObject(ads),
            });
          }
        }

        if (learningPartners?.slot) {
          const ads = await loadAds(learningPartners.slot);

          if (ads) {
            store.dispatch({
              type: 'Ads/set_learning_partner',
              payload: removedUndefinedFromArrayObject(ads),
            });
          }
        }
      } catch (error) {
        console.log(
          '🚀 ~ file: [slug].tsx:277 ~ store=> ~ tag jobs error:',
          error
        );

        return {
          notFound: true,
        };
      }
    }
    if (!isAppIimjobs) {
      const CATEGORY_MAPPED =
        HIRIST_CATEGORY_BLOG_ID_MAPPING[
          HIRIST_CATEGORY_ID_MAPPING[catId?.toString() || ''] ?? catId
        ];
      const blogs = await jobfeedService.getLatestBlogs(CATEGORY_MAPPED ?? '');
      const bloData = (Object.values(blogs) as AdsElementType[])
        ?.map(blog => {
          return {
            link: blog?.link,
            title: blog?.title,
            imageUrl: blog?.featuredImage ?? '',
          };
        })
        ?.slice(0, 3);

      store.dispatch({
        type: 'Ads/set_blog_data',
        payload: bloData,
      });
    }

    const seoConfig = await loadSEO(
      TAG,
      getListSEOContent(slugTitle || slugToTitle(tabSlug), pageNo, totalJobs),
      getPageUrl(slugUrl, pageNo),
      true,
      pageNo
    );

    const titleInfo = slugTitle || slugToTitle(tabSlug);

    store.dispatch({
      type: 'AppContext/set_slug',
      payload: titleInfo,
    });

    return {
      props: {
        userIdSSR: userId,
        tagTitle: titleInfo,
        tagId: slugId || '',
        catId,
        metaData,
        seoConfig,
        filters,
      },
    };
  }
);
export default TagJobFeedPage;
